import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {observer} from 'mobx-react-lite';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckCircle, faCircleExclamation, faInfoCircle, faXmarkCircle} from '@fortawesome/pro-duotone-svg-icons';
import {Empty, Tooltip} from 'antd';
import {faPlus, faTrash, faSpinnerThird} from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import {useStore} from '@/store/root-store';
import {useRouter} from 'next/router';

const getNotificationIcon = (type: string) => {
  if (type === 'success') {
    return <FontAwesomeIcon className='icon' icon={faCheckCircle} color='#2AC155' fontSize={40}/>;
  } else if (type === 'error') {
    return <FontAwesomeIcon className='icon' icon={faXmarkCircle} color='#F44343' fontSize={40}/>;
  } else if (type === 'info') {
    return <FontAwesomeIcon className='icon' icon={faInfoCircle} color='#2D6CCA' fontSize={40}/>;
  } else if (type === 'warning') {
    return <FontAwesomeIcon className='icon' icon={faCircleExclamation} color='#F1AA3E' fontSize={40}/>;
  }
};
interface Props {
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}
export const GeneralTabContent = observer(({setVisible}: Props) => {
  const {settings: {customer: {loadUserOnboardingData, deleteUserNotification, getGeneralNotifications, updateOnBoardingNotificationData}}} = useStore('');
  const [deletingNotification, setDeletingNotification] = useState<any>(-1);
  const [generalNotification, setGeneralNotification] = useState([]);
  const router = useRouter();
  useEffect(() => {
    if (getGeneralNotifications?.length) {
      const unreadNotifications = getGeneralNotifications.filter(item => item?.status === 'unread');
      const readNotifications = getGeneralNotifications.filter(item => item?.status === 'read');
      setGeneralNotification([...unreadNotifications, ...readNotifications]);
    }
  }, [getGeneralNotifications]);
  const onDeleteGeneral = async id => {
    setDeletingNotification(`delete-${id}`);
    try {
      await deleteUserNotification(id);
      await loadUserOnboardingData({channels: 'general'});
      setDeletingNotification(-1);
    } catch (error) {
      setDeletingNotification(-1);
      Promise.reject(error);
    }
  };
  const onButtonClick = async item => {
    await updateOnBoardingNotificationData(item?.id, {status: 'read'});
    await loadUserOnboardingData({channels: 'general'});
    setVisible(false);
    router.push(item?.action?.url);
  };
  return (
    <Wrapper>
      {generalNotification?.length > 0 ? (
        generalNotification?.map((item, idx) => (
          <div onClick={() => {}} className={`notification-item ${item?.status === 'unread' ? 'unread-notification' : ''}`} key={idx}>
            <div className='item-content'>
              <Tooltip title={item?.createdAt && moment(item?.createdAt).format('MMMM D, YYYY [at] h:mm A')} placement='topRight'>
                <div className='notification-icon-wrapper'>
                  {getNotificationIcon(item.type)}
                  {item?.status === 'unread' && <div className='unread-icon'/>}
                </div>
              </Tooltip>
              <div>
                <h4 className='notification-title'>
                  {item?.title}
                </h4>
                <p className={item?.status === 'unread' ? 'unread-description' : ''}>{item?.body}</p>
                {item?.action && <StyledButton className={item?.status === 'unread' ? 'unread-button' : ''} onClick={() => onButtonClick(item)}>
                  <FontAwesomeIcon icon={faPlus} color='#F1AA3E' fontSize={12}/>{item?.action?.label}
                </StyledButton>}
              </div>
            </div>
            <FontAwesomeIcon
              className={`delete-icon ${deletingNotification === `delete-${item?.id}` ? 'disabled fa-spin' : ''}`}
              icon={deletingNotification === `delete-${item?.id}` ? faSpinnerThird : faTrash}
              fontSize={14} color='rgb(163, 164, 164)'
              onClick={() => deletingNotification === -1 && onDeleteGeneral(item?.id)}
            />
          </div>
        ))
      ): (
        <EmptyStyled description='No New Notifications' />
      )}
    </Wrapper>
  );
});


const Wrapper = styled.div`
  max-height: 416px;
  overflow: hidden;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 5px;
    height: 50px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(78, 81, 86, 0.4);
    border-radius: 10px;
  }
  .notification-item {
    padding: 16px 14px;
    border-bottom: 1px solid #D9D9D9;
    position: relative;
    background-color: #fdfdfe;
    &:hover {
      background-color: #f9f9fb;
      .delete-icon {
        display: block;
      }
    }
    .delete-icon {
      position: absolute;
      top: 12px;
      right: 16px;
      cursor: pointer;
      &.disabled {
        cursor: pointer;
        &:hover {
          color: rgba(163, 164, 164, 80%);
        }
      }
      display: none;
      &:hover {
        color: red;
      }
    }
    .item-content {
      display: flex;
      gap: 13px;
      .notification-icon-wrapper {
        position: relative;
        .unread-icon {
          width: 8px;
          height: 8px;
          background-color: #f1aa3e;
          border-radius: 30px;
          position: absolute;
          top: -5px;
          left: -5px;
        }
      }
      div {
        .notification-title {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 15px;
          margin-bottom: 4px;
          color: #121212;
          display: flex;
          width: 94%;
        }
        p {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 14px;
          margin-bottom: 0px;
          &.unread-description {
            color: #121212;
            font-weight: 500;
          }
        }
      }
    }
    &.unread-notification {
      background-color: #ececec;
    }
  }
`;

const EmptyStyled = styled(Empty)`
  color: rgba(0, 0, 0, 0.25);
  height: 200px;
  padding-top: 30px;
`;
const StyledButton = styled.div<{width?: string | number; borderColor?: string; backgroundColor?: string; disabled?: boolean}>`
  margin-top: 6px;
  border: ${p => `1px solid ${p?.borderColor ? p?.borderColor : '#CDCDCD'}`};
  background-color: ${p => p?.backgroundColor ? p?.backgroundColor : '#ffffff'};
  width: ${p => p?.width ? p?.width : 'fit-content'};
  padding: 5px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: ${p => p?.disabled ? 'not-allowed' : 'pointer'};
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 14.52px;
  &.unread-button {
    color: #121212;
    font-weight: 500;
  }
`;
